import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CircleLoader from 'react/components/CircleLoader';
import MaterialIcon from 'react/elements/MaterialIcon';
import findDuplicates, { DUPLICATE_WINDOW_DAYS } from 'utilities/findDuplicates';
import { moneyFormatFromCents } from 'utilities/moneyFormat';
import { fetchNotDuplicatePairs, markNotDuplicates } from 'redux/actions/transactions';
import { setTransactionToEditMode } from 'redux/actions/dashboard';

const plural = (count, one, many) => (count === 1 ? `1 ${one}` : `${count} ${many}`);

/**
 * The transactions page's Duplicates tab: transactions that may be one entered twice, from every
 * month, likeliest first (findDuplicates). Each group can be settled by keeping one of its
 * transactions, which deletes the others as the page's own delete does, or by marking it not
 * duplicates, which is kept on the server (budget/mark_not_duplicates) so the group stays away.
 * A transaction opens in the edit popup, for a group that needs more than either. A keep can be
 * undone from the notice it leaves, which brings the deleted ones back.
 *
 * Groups marked not duplicates are listed under the others, folded, each with a way back.
 */
export default function DuplicateTransactions({ transactionDelete, transactionDeleteUndo }) {
	const dispatch = useDispatch();
	const transactionFinder = useSelector((state) => state.dashboard.transactionFinder);
	const transactions = useSelector((state) => state.dashboard.transactions);
	const transactionFinderUpdates = useSelector((state) => state.dashboard.transactionFinderUpdates);
	const categories = useSelector((state) => state.dashboard.categories);
	const [pairs, setPairs] = useState(null);
	const [showDismissed, setShowDismissed] = useState(false);
	const [busy, setBusy] = useState(null);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	// Bumped once a group is settled, so the groups are found again whether or not the delete
	// reached the store's own counters yet.
	const [settledCount, setSettledCount] = useState(0);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		fetchNotDuplicatePairs()
			.then((list) => mounted.current && setPairs(list))
			.catch(() => mounted.current && setError('The duplicates did not load. Try again.'));
		return () => {
			mounted.current = false;
		};
	}, []);

	const { groups, dismissed } = useMemo(
		() => (pairs === null ? { groups: [], dismissed: [] } : findDuplicates(transactionFinder.getAllTransactions(), pairs)),
		[pairs, transactions, transactionFinderUpdates, settledCount]
	);

	const settle = (group, work, failure) => {
		setBusy(group.key);
		setError(null);
		setNotice(null);
		return work().then((result) => {
			if(mounted.current) {
				setBusy(null);
				setNotice(result || null);
				setSettledCount((count) => count + 1);
			}
		}).catch(() => {
			if(mounted.current) {
				setBusy(null);
				setError(failure);
			}
		});
	};

	const keep = (group, kept) => settle(group, () => {
		const others = group.transactions.filter((each) => each !== kept);
		return Promise.all(others.map((each) => transactionDelete(each))).then(() => ({
			text: `Deleted ${plural(others.length, 'transaction', 'transactions')} and kept ${kept.where || 'the one'} on ${kept.transaction_date.format('MMM D')}.`,
			undo: () => settle(group, () => Promise.all(others.map((each) => transactionDeleteUndo(each)))
				.then(() => ({ text: `Brought back ${plural(others.length, 'transaction', 'transactions')}.` })),
			'They could not be brought back. Try again.'),
		}));
	}, 'The others could not be deleted. Try again.');

	const mark = (group, notDuplicates = true) => settle(group, () => markNotDuplicates(group.transactions.map((each) => each.id), notDuplicates)
		.then((list) => {
			if(mounted.current) {
				setPairs(list);
			}
		}), notDuplicates ? 'That could not be saved. Try again.' : 'That group could not be brought back. Try again.');

	const open = (transaction) => dispatch(setTransactionToEditMode(transaction.clone()));
	const categoryName = (id) => {
		const category = id && categories && categories.get(id);
		return category ? category.name : null;
	};

	return (
		<div className='duplicate_cleanup'>
			<p className='duplicate_cleanup__intro'>
				Transactions of the same amount within {DUPLICATE_WINDOW_DAYS} days of each other, from every month.
				Keep the one that is right and the others are deleted, or mark them as not duplicates.
			</p>
			{!!error && <p className='duplicate_cleanup__error red-text'>{error}</p>}
			{!!notice &&
			<p className='duplicate_cleanup__notice'>
				{notice.text}
				{!!notice.undo && !!transactionDeleteUndo &&
				<button type='button' className='btn-flat duplicate_cleanup__undo' disabled={busy !== null} onClick={notice.undo}>
					Undo
				</button>
				}
			</p>
			}

			{pairs === null && !error && <CircleLoader />}
			{pairs !== null && groups.length === 0 &&
			<p className='duplicate_cleanup__empty'>No duplicates found.</p>
			}
			{groups.length > 0 &&
			<ul className='duplicate_cleanup__list'>
				{groups.map((group) =>
				<DuplicateGroup
					key={group.key}
					group={group}
					busy={busy === group.key}
					categoryName={categoryName}
					onOpen={open}
					transactionAction={(transaction) =>
					<button type='button' className='btn-flat duplicate_cleanup__keep' disabled={busy === group.key} onClick={() => keep(group, transaction)}>
						<MaterialIcon>check</MaterialIcon>
						Keep this one
					</button>
					}
				>
					<button type='button' className='btn-flat duplicate_cleanup__dismiss' disabled={busy === group.key} onClick={() => mark(group)}>
						<MaterialIcon>call_split</MaterialIcon>
						Not duplicates
					</button>
				</DuplicateGroup>
				)}
			</ul>
			}

			{pairs !== null &&
			<button type='button' className='btn-flat duplicate_cleanup__dismissed-toggle' aria-expanded={showDismissed} onClick={() => setShowDismissed(!showDismissed)}>
				<MaterialIcon>{showDismissed ? 'expand_less' : 'expand_more'}</MaterialIcon>
				Marked not duplicates ({dismissed.length})
			</button>
			}
			{showDismissed && dismissed.length === 0 &&
			<p className='duplicate_cleanup__empty'>Nothing marked.</p>
			}
			{showDismissed && dismissed.length > 0 &&
			<ul className='duplicate_cleanup__list is-dismissed'>
				{dismissed.map((group) =>
				<DuplicateGroup
					key={group.key}
					group={group}
					busy={busy === group.key}
					categoryName={categoryName}
					onOpen={open}
				>
					<button type='button' className='btn-flat duplicate_cleanup__restore' disabled={busy === group.key} onClick={() => mark(group, false)}>
						<MaterialIcon>undo</MaterialIcon>
						Bring back
					</button>
				</DuplicateGroup>
				)}
			</ul>
			}
		</div>
	);
}

/**
 * One group: its amount, how sure the match is, and its transactions. `transactionAction` draws what
 * each transaction offers, and `children` are what the group offers.
 */
function DuplicateGroup({ group, busy, categoryName, onOpen, transactionAction = null, children }) {
	const amount = group.transactions[0].amount;

	return (
		<li className={`duplicate_cleanup__group${busy ? ' is-busy' : ''}`}>
			<div className='duplicate_cleanup__heading'>
				<strong>{moneyFormatFromCents(Math.abs(amount))}</strong>
				<span className={`duplicate_cleanup__badge${group.likely ? ' is-likely' : ''}`}>
					{group.likely ? 'Likely duplicate' : 'Possible duplicate'}
				</span>
			</div>
			<ul className='duplicate_cleanup__transactions'>
				{group.transactions.map((transaction) =>
				<li key={transaction.id} className='duplicate_cleanup__transaction'>
					<button type='button' className='btn-flat duplicate_cleanup__open' onClick={() => onOpen(transaction)}>
						<span className='duplicate_cleanup__where'>{transaction.where || 'No name'}</span>
						<span className='duplicate_cleanup__muted'>
							{transaction.transaction_date.format('ddd, MMM D, YYYY')}
							{!!categoryName(transaction.category_id) && ` · ${categoryName(transaction.category_id)}`}
							{!!transaction.plaid_transaction_id && ' · from a bank record'}
						</span>
						{!!transaction.description &&
						<span className='duplicate_cleanup__muted duplicate_cleanup__description'>{transaction.description}</span>
						}
					</button>
					{!!transactionAction && transactionAction(transaction)}
				</li>
				)}
			</ul>
			<div className='duplicate_cleanup__actions'>
				{children}
			</div>
		</li>
	);
}
